import React, { useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { AlertCircle } from 'lucide-react';
import { BUBBLE_SITE_URL } from '../../services/api';
import { LOGO_URL } from '../../constants';

// Password resets are handled by the Bubble site's login page.
const FORGOT_PASSWORD_URL = `${BUBBLE_SITE_URL}/login?forgotpassword`;

const Login: React.FC = () => {
  const { loginWithPassword, error: authError } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!email.trim() || !password) {
      setError('Please enter your email and password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      await loginWithPassword(email.trim(), password);
    } catch (err) {
      setError('Email or password not found. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: 'linear-gradient(to bottom right, #f5f8ef, #e8f0d9)' }}>
      <div className="bg-white p-8 rounded-lg shadow-lg max-w-md w-full">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-4">
            <img
              src={LOGO_URL}
              alt="SmartFilterPro"
              className="h-16"
            />
          </div>
          <p className="text-gray-600">Access your dashboard</p>
        </div>

        {(error || authError) && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-start">
            <AlertCircle className="h-5 w-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-sm text-red-800 font-medium">Authentication Error</p>
              <p className="text-sm text-red-700 mt-1">{error || authError}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
              Email
            </label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              style={{ '--tw-ring-color': '#b5c989' } as React.CSSProperties}
              disabled={loading}
              autoComplete="email"
            />
          </div>

          <div className="mb-6">
            <div className="flex items-baseline justify-between mb-2">
              <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                Password
              </label>
              <a
                href={FORGOT_PASSWORD_URL}
                className="text-sm font-medium hover:underline"
                style={{ color: '#2f383a' }}
              >
                Forgot password?
              </a>
            </div>
            <input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:border-transparent"
              style={{ '--tw-ring-color': '#b5c989' } as React.CSSProperties}
              disabled={loading}
              autoComplete="current-password"
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full text-white py-3 px-4 rounded-lg font-medium focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all hover:shadow-md"
            style={{ backgroundColor: '#b5c989' }}
          >
            {loading ? 'Authenticating...' : 'Sign In'}
          </button>
        </form>

        <div className="mt-6 text-center">
          <p className="text-sm text-gray-600">
            Don't have access?{' '}
            <a
              href="https://smartfilterpro-scaling.bubbleapps.io"
              className="font-medium hover:underline"
              style={{ color: '#2f383a' }}
              target="_blank"
              rel="noopener noreferrer"
            >
              Sign up here
            </a>
          </p>
        </div>
      </div>
    </div>
  );
};

export default Login;
